import { equals } from '@execonline-inc/collections';
import { action, makeObservable, observable } from 'mobx';
import { ExperienceResource } from '../../Types';
import { Section } from '../Types';

const sameExperienceStoreAs =
  (store: LearningDesignExperienceStore) => (otherStore: LearningDesignExperienceStore) =>
    equals(store.experienceResource.payload.id, otherStore.experienceResource.payload.id);

const sameExperienceStores = (
  sections: ReadonlyArray<Section>,
  experienceStore: LearningDesignExperienceStore,
) =>
  sections
    .flatMap(({ learningDesignExperiences: { experienceStores, deletedExperienceStores } }) => [
      ...experienceStores,
      ...deletedExperienceStores,
    ])
    .filter(sameExperienceStoreAs(experienceStore));

class LearningDesignExperienceStore {
  readonly experienceResource: ExperienceResource;

  @observable
  selectedIds: number[] = [];

  @observable
  previouslySelectedIds: number[] = [];

  constructor(experienceResource: ExperienceResource) {
    makeObservable(this);
    this.experienceResource = experienceResource;
  }

  @action
  setSelectedIdsForAllLikeThis = (selectedIds: number[], sections: ReadonlyArray<Section>) => {
    this.setSelectedIds(selectedIds);
    sameExperienceStores(sections, this).forEach((s) => {
      s.setSelectedIds(selectedIds);
    });
  };

  @action
  setSelectedIds = (selectedIds: number[]) => {
    this.selectedIds = selectedIds;
  };

  @action
  setPreviouslySelectedIdsForAllLikeThis = (
    selectedIds: number[],
    sections: ReadonlyArray<Section>,
  ) => {
    this.setPreviouslySelectedIds(selectedIds);
    sameExperienceStores(sections, this).forEach((s) => {
      s.setPreviouslySelectedIds(selectedIds);
    });
  };

  @action
  setPreviouslySelectedIds = (selectedIds: number[]) => {
    this.previouslySelectedIds = selectedIds;
  };

  represents = (experienceResource: ExperienceResource): boolean =>
    experienceResource.payload.id === this.experienceResource.payload.id;

  representsAny = (experienceResources: ExperienceResource[]): boolean => {
    const resourceIds = experienceResources.map((filterExperience) => filterExperience.payload.id);
    return !resourceIds.includes(this.experienceResource.payload.id);
  };
}

export default LearningDesignExperienceStore;
